import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Copy } from "lucide-react";

/* THE ONE PLACE A SHOWN-ONCE SECRET IS DISPLAYED.
 *
 * Born out of 2026-10-01: an admin reset his own password from the Users list, the list refresh
 * behind the dialog returned 401, the interceptor redirected, and the one-time passphrase was gone
 * before he could read a word of it. He was locked out of the only Admin account on the live app.
 * The same reveal existed in three places, built three different ways — a second door nobody knew
 * about, and a third that showed the passphrase in a native alert you cannot copy text out of.
 *
 * His rule: one implementation, so a future fix lands everywhere at once. The rules it enforces:
 *   · nothing closes it until "I have saved this" is ticked — not Escape, not a click outside, and
 *     the X is hidden until then
 *   · the caller must NOT refresh anything behind it; the refresh belongs in onClose
 *   · the value is always copyable
 */
export function OneTimeReveal({ open, title, intro, value, codes, onClose, testid = "one-time-reveal" }) {
  const [saved, setSaved] = useState(false);

  const close = () => { setSaved(false); onClose(); };
  const copy = () => {
    navigator.clipboard.writeText(codes ? codes.join("\n") : value);
    toast.success("Copied");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && saved) close(); }}>
      <DialogContent
        className={`rounded-lg border-slate-200 sm:max-w-md ${saved ? "" : "[&>button]:hidden"}`}
        onEscapeKeyDown={(e) => { if (!saved) e.preventDefault(); }}
        onInteractOutside={(e) => { if (!saved) e.preventDefault(); }}
        data-testid={testid}>
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {intro && <p className="text-sm text-slate-600" data-testid={`${testid}-intro`}>{intro}</p>}
          {codes ? (
            <div className="bg-slate-900 rounded-lg p-3 grid grid-cols-2 gap-1" data-testid={`${testid}-codes`}>
              {codes.map((c, i) => <code key={i} className="text-emerald-300 font-mono text-sm">{c}</code>)}
            </div>
          ) : (
            <code data-testid={`${testid}-value`} className="block bg-slate-900 text-emerald-300 font-mono text-base p-3 rounded-lg break-all">{value}</code>
          )}
          <Button size="sm" variant="outline" data-testid={`${testid}-copy`} className="rounded-lg border-slate-200" onClick={copy}>
            <Copy className="w-3.5 h-3.5 mr-1" />Copy
          </Button>
          <p className="text-[11px] text-amber-600">⚠ Not emailed, not stored in readable form. Once you close this, it cannot be retrieved.</p>
          <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 cursor-pointer">
            <Checkbox data-testid={`${testid}-saved-checkbox`} checked={saved} onCheckedChange={(v) => setSaved(!!v)} className="mt-0.5" />
            <span className="text-xs text-amber-900 leading-snug">
              <b>I have saved this.</b> Until this is ticked, this box will not close — not by Escape, not by clicking away.
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button data-testid={`${testid}-done`} disabled={!saved} onClick={close} className="rounded-lg bg-[#1F1F1F] text-white">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
